// src/pages/UninstallFeedback.js
// Opened by Chrome when the extension is removed (chrome.runtime.setUninstallURL in
// background.js). Anonymous: the extension's session is gone by now.
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Mascot from '../components/Mascot';
import { endpoints, CHROME_STORE_URL } from '../services/api';
import '../styles/pages/Page.css';
import '../styles/pages/LegalPage.css';
import '../styles/components/StatePage.css';

const REASONS = [
  'It didn’t work on the site I needed',
  'Playback couldn’t find the right element',
  'Recording missed steps or got them wrong',
  'It was hard to figure out',
  'Privacy or security concerns',
  'It slowed down my browser',
  'I only needed it once',
  'I switched to another tool',
  'Something else',
];

export default function UninstallFeedback() {
  const [params] = useSearchParams();
  const [reasons, setReasons] = useState([]);
  const [details, setDetails] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent
  const [error, setError] = useState('');

  const toggle = (r) => setReasons((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reasons.length && !details.trim()) {
      setError('Pick a reason or write a line, so we know what to fix.');
      return;
    }
    setError('');
    setStatus('sending');
    try {
      const res = await fetch(endpoints.feedback.uninstall, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reasons,
          details: details.trim(),
          email: email.trim() || null,
          version: params.get('v'),
        }),
      });
      if (!res.ok) throw new Error(res.status === 429 ? 'rate' : 'failed');
      ReactGA.event('extension_uninstall_feedback', { reasons: reasons.join(' | ') });
      setStatus('sent');
    } catch (err) {
      setError(err.message === 'rate'
        ? 'You’ve sent a lot of feedback in the last hour. Try again later.'
        : 'We couldn’t send that. Try again, or email michii.support@gmail.com.');
      setStatus('idle');
    }
  };

  return (
    <div className="page-container">
      <div className="container legal-container">
        <div className="contact-grid">
          <section className="contact-intro">
            <div className="legal-header">
              <p className="m-eyebrow">Michii was removed</p>
              <h1 className="page-title">Sorry to see you go.</h1>
            </div>
            <p>
              Tell us what went wrong. It takes 20 seconds, and every answer is read
              by the people who build Michii.
            </p>
            <div className="state-say">
              <Mascot size={40} />
              <div className="state-say__bubble">
                <b>Michii says</b>
                <span>Your saved guides are still in your account.</span>
              </div>
            </div>
          </section>

          {status === 'sent' ? (
            <div className="contact-success m-sheet" role="status">
              <span className="contact-success-icon" aria-hidden="true">✓</span>
              <h3>Thanks. That helps.</h3>
              <p>If you left an email, we may write back with a fix.</p>
              <a href={CHROME_STORE_URL} className="lp-tlink" target="_blank" rel="noopener noreferrer">
                Changed your mind? Reinstall Michii
              </a>
            </div>
          ) : (
            <section className="contact-sheet m-sheet">
              {error && <p className="contact-error" role="alert">{error}</p>}
              <form className="contact-form" onSubmit={handleSubmit}>
                <fieldset className="uf-reasons">
                  <legend className="contact-label">Why did you uninstall? Pick any that apply.</legend>
                  {REASONS.map((r) => (
                    <label key={r} className="uf-reason">
                      <input type="checkbox" checked={reasons.includes(r)} onChange={() => toggle(r)} />
                      <span>{r}</span>
                    </label>
                  ))}
                </fieldset>

                <div className="contact-field">
                  <label htmlFor="uf-details" className="contact-label">
                    What happened? <span className="contact-label__hint">(optional)</span>
                  </label>
                  <textarea
                    id="uf-details"
                    className="contact-input contact-textarea uf-textarea"
                    placeholder="The site, the step, what you expected…"
                    rows={3}
                    maxLength={2000}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="uf-email" className="contact-label">
                    Email <span className="contact-label__hint">(optional, only if you want a reply)</span>
                  </label>
                  <input
                    id="uf-email"
                    type="email"
                    className="contact-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <button type="submit" className="lp-btn contact-submit" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Send feedback'}
                </button>
              </form>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
